import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from './utils';

// Resilient room-list loader for the kiosk.
// The old pattern fetched the room list ONCE on mount: if the tablet's page
// loaded while Wi-Fi was down, the list stayed empty forever and EVERY
// check-in was rejected with "Room X is not valid" until a manual reload.
// This hook retries on failure (2s/4s/8s then every 30s), refreshes when the
// connection comes back, and refreshes every 5 minutes to stay current.
export function useRoomList() {
  const [rooms, setRooms] = useState([]);
  const [roomsLoaded, setRoomsLoaded] = useState(false);
  const retryTimer = useRef(null);
  const mounted = useRef(true);

  const fetchRooms = useCallback(async (retryCount = 0) => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    try {
      const response = await axios.get(`${API}/rooms`, { timeout: 10000 });
      if (!mounted.current) return;
      setRooms(response.data || []);
      setRoomsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
      if (!mounted.current) return;
      const delay = retryCount < 3 ? 2000 * Math.pow(2, retryCount) : 30000;
      retryTimer.current = setTimeout(() => fetchRooms(retryCount + 1), delay);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchRooms();
    const onOnline = () => fetchRooms();
    window.addEventListener('online', onOnline);
    const refreshInterval = setInterval(() => fetchRooms(), 5 * 60 * 1000);
    return () => {
      mounted.current = false;
      window.removeEventListener('online', onOnline);
      clearInterval(refreshInterval);
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, [fetchRooms]);

  return { rooms, roomsLoaded };
}
